import React from 'react';
import cx from 'classnames';
import { Card } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import { IState } from 'interfaces/redux/IState';
import { CSL, FC } from 'constants/index';
import { ICounselingTask } from 'interfaces/redux/task-types/ICounselingTask';
import { IFillCoordinationTask } from 'interfaces/redux/task-types/IFillCoordinationTask';
import FillCoordinationTransition from '../../containers/tasks/fill-coordination/fill-coordination-transition/fill-coordination-transition';
import CounselingTransition from '../../containers/tasks/counseling/counseling-transition/counseling-transition';
import { IProps } from './interfaces/IProps';
import createLazyOnscreenRender from '../../containers/patient/tasks-new/create-lazy-onscreen-render';

const useStyles = makeStyles(() => ({
  wrapper: {
    boxShadow: 'none',
  },
}));

// This factory should return a component that can change the form
// based on the status of the task.
const SingleForm = (props: IProps) => {
  const classes = useStyles();
  const allTasks = useSelector((state: IState) => state.tasks.data);
  const {
    tasks,
    taskWrapperClass,
    cancelClick,
    legacyFormProps,
    onSelectTaskIds: selectTasks,
    therapies,
    handleOnSelectedRow,
  } = props;

  switch (tasks?.[0]?.taskType) {
    case CSL:
      return (
        <Card className={cx(taskWrapperClass, classes.wrapper)} style={{ width: '100%' }}>
          <CounselingTransition
            therapyIndex={null}
            tasks={tasks.map(task => task as ICounselingTask)}
            legacyFormProps={legacyFormProps}
            legacyCancelClick={cancelClick}
            tab="tasks"
            therapies={therapies}
          />
        </Card>
      );
    case FC: {
      return (
        <FillCoordinationTransition
          handleOnSelectedRow={handleOnSelectedRow}
          tasks={tasks as IFillCoordinationTask[]}
          legacyFormProps={legacyFormProps as any}
          legacyCancelClick={cancelClick}
          onSelectTaskIds={selectTasks}
          taskWrapperClass={taskWrapperClass}
          therapies={therapies}
        />
      );
    }
    case 'Order': {
      const fc_tasks: IFillCoordinationTask[] = [];
      tasks.forEach(task => {
        task.fc_ids?.split(',').forEach((fc_id: string) => {
          const fcTask = allTasks[`${FC}${fc_id}`] as IFillCoordinationTask;
          if (fcTask) {
            fc_tasks.push(fcTask);
          }
        });
      });
      return (
        <FillCoordinationTransition
          handleOnSelectedRow={handleOnSelectedRow}
          tasks={fc_tasks as IFillCoordinationTask[]}
          legacyFormProps={legacyFormProps as any}
          legacyCancelClick={cancelClick}
          onSelectTaskIds={selectTasks}
          taskWrapperClass={taskWrapperClass}
          therapies={therapies}
          isAnOrderTask
        />
      );
    }
    default:
      return null;
  }
};

const MultipleForms = (props: IProps) => {
  const {
    tasks,
    taskWrapperClass,
    cancelClick,
    legacyFormProps,
    onSelectTaskIds: selectTasks,
    taskStatusProcessor,
    therapies,
    handleOnSelectedRow,
  } = props;
  const allTasks = useSelector((state: IState) => state.tasks.data);

  switch (tasks?.[0]?.taskType) {
    case CSL:
      // eslint-disable-next-line no-case-declarations
      const counselingStatusGroups = taskStatusProcessor.groupTasksByStatus(tasks);
      return (
        <>
          {Object.keys(counselingStatusGroups)
            .filter(group => counselingStatusGroups[group].length)
            .map(group => (
              <CounselingTransition
                therapyIndex={null}
                tasks={counselingStatusGroups[group] as ICounselingTask[]}
                legacyFormProps={legacyFormProps}
                legacyCancelClick={cancelClick}
                tab="tasks"
                therapies={therapies}
              />
            ))}
        </>
      );
    case FC:
      // eslint-disable-next-line no-case-declarations
      const fcStatusGroups = taskStatusProcessor.groupTasksByStatus(tasks);
      return (
        <>
          {Object.entries(fcStatusGroups)
            .filter(([, value]) => value.length)
            .map(([, value]) => {
              const taskIds = value.map(x => x.id);
              const updatedLegacyProps = {
                ...legacyFormProps,
                taskIds: value.map(x => x.id),
                combinedTask: {
                  [FC]: legacyFormProps.combinedTask[FC].filter((x: any) => taskIds.includes(x.id)),
                },
              };
              return (
                <FillCoordinationTransition
                  handleOnSelectedRow={handleOnSelectedRow}
                  tasks={value as IFillCoordinationTask[]}
                  legacyFormProps={updatedLegacyProps as any}
                  legacyCancelClick={cancelClick}
                  onSelectTaskIds={selectTasks}
                  taskWrapperClass={taskWrapperClass}
                  therapies={therapies}
                />
              );
            })}
        </>
      );
    case 'Order':
      // eslint-disable-next-line no-case-declarations
      const orderStatusGroups = taskStatusProcessor.groupTasksByStatus(tasks);
      return (
        <>
          {Object.entries(orderStatusGroups)
            .filter(([, value]) => value.length)
            .map(([, value]) => {
              const fc_tasks: IFillCoordinationTask[] = [];
              value.forEach(task => {
                task.fc_ids?.split(',').forEach((fc_id: string) => {
                  const fcTask = allTasks[`${FC}${fc_id}`] as IFillCoordinationTask;
                  if (fcTask) {
                    fc_tasks.push(fcTask);
                  }
                });
              });
              const task_Ids = fc_tasks.map(x => x.id);
              const { taskType, taskIds, combinedTask, ...restLegacyProps } =
                legacyFormProps as any;
              const modifiedLegacyFormProps = {
                ...restLegacyProps,
                taskType: 'FC', // Pass FC to the legacy form so it renders the correct form
                taskIds: task_Ids,
                combinedTask: {
                  [FC]: taskIds.map((id: number) => allTasks[`${FC}${id}`]).filter((x: any) => !!x),
                },
              };
              return (
                <FillCoordinationTransition
                  handleOnSelectedRow={handleOnSelectedRow}
                  tasks={fc_tasks as IFillCoordinationTask[]}
                  legacyFormProps={modifiedLegacyFormProps as any}
                  legacyCancelClick={cancelClick}
                  onSelectTaskIds={selectTasks}
                  taskWrapperClass={taskWrapperClass}
                  therapies={therapies}
                />
              );
            })}
        </>
      );
    default:
      return null;
  }
};

export default {
  SingleForm: createLazyOnscreenRender(SingleForm),
  MultipleForms: createLazyOnscreenRender(MultipleForms),
};
